// What an approver reads off a request's sandbox (LO-185): the QA report, the
// kernel call log and the egress log, shaped for the Requests screen.
//
// Plain functions and no React, so the rules are testable without a browser and
// the screen stays about layout. Nothing here keeps a second copy of a rule the
// kernel decides: a verdict, a pass label, a refusal's wording and the sentences
// about what a log does not prove all arrive in the responses and are passed
// through. What lives here is only ordering ("failing first"), pairing declared
// with observed, and the choice not to flag a grant from a log that is empty.

// -- the one phrase a pass is allowed to be called ------------------------------------

/**
 * What a pass is called, said when the report did not carry its own `label`.
 *
 * The kernel sends this string (`sandbox_qa.LABEL`) on every passing report and
 * the screen shows what it was sent. This is the fallback for a report that
 * arrived without it, and it is the same claim: a pass is about synthetic data
 * and the declared personas, and is never worded as a safety claim.
 */
export const PASS_LABEL = "passed against synthetic data and these personas";

// -- reading a part -------------------------------------------------------------------

/** One of the three reads on a request, as the hook keeps it. */
export const loading = () => ({ status: "loading" });
export const loaded = (body) => ({ status: "ok", body });
export const failed = (error) => ({ status: "failed", error });

/**
 * A read that failed, sorted into the sentences a reader needs apart.
 *
 *  - **closed**: the kernel answered 404 `sandbox_closed` or `no_sandbox`. There
 *    is no sandbox to read, and its own `detail` says why. Not an error.
 *  - **refused**: the kernel said this console, or this person, may not ask.
 *    `isManifest` is the console's own ceiling and is rendered by the kit's
 *    `Refusal`; any other 401/403 is a person's.
 *  - **error**: anything else: a network or a bug, worth a retry.
 */
export function failureKind(error) {
  const status = Number(error?.status || 0);
  const code = String(error?.error || error?.body?.error || "");
  if (status === 404 && ["sandbox_closed", "no_sandbox"].includes(code)) return "closed";
  if (error?.isManifest || status === 401 || status === 403) return "refused";
  return "error";
}

// -- the QA report --------------------------------------------------------------------

const CHECK_ORDER = { fail: 0, skipped: 1, pass: 2 };
const PERSONA_ORDER = { failed: 0, error: 1, skipped: 2, passed: 3 };

/** `checks`, failing first, then skipped, then passing. Stable inside each. */
export function sortChecks(checks) {
  return (Array.isArray(checks) ? checks : [])
    .map((check, index) => ({ check, index }))
    .sort((a, b) =>
      (CHECK_ORDER[a.check?.status] ?? 1) - (CHECK_ORDER[b.check?.status] ?? 1)
      || a.index - b.index)
    .map(({ check }) => check);
}

const only = (checks, status) => (checks || []).filter((c) => c?.status === status);

function screenshotsOf(persona) {
  const seen = new Set();
  const out = [];
  const add = (path, label) => {
    const key = String(path || "");
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({ path: key, label: String(label || "") });
  };
  for (const s of persona?.screenshots || []) add(s?.path, s?.label);
  for (const r of persona?.routes || []) add(r?.screenshot, r?.route || r?.title);
  return out;
}

/**
 * Whether the kernel said a persona may open the app (LO-211), for the approver: what the persona
 * lacks, by its own `my-access` answer, and what the run did about it. Empty for a persona that has
 * access (the usual one) and for a report from before the kernel said. A persona whose answer was not
 * known is said to have been explored as one who may open the app, which is what the run did.
 */
export function accessLine(access) {
  if (!access || typeof access !== "object") return "";
  if (access.has_access === false) {
    const lacks = (access.missing || [])
      .map((m) => `${m.tag} (${m.access ? `${m.access} ${m.type}` : m.type})`).join(", ") || "access it could not name";
    return `Lacks ${lacks}, by its own my-access answer. The run expected the missing-permissions screen naming exactly that, looked at that one screen and pressed nothing on it.`;
  }
  if (access.has_access === null || access.has_access === undefined) {
    return `Whether it may open the app was not known (${access.detail || "the kernel was not asked"}), so it was explored as one who may.`;
  }
  return "";
}

/** One persona's block of the report, ordered the way it is read. */
function personaView(persona) {
  const checks = sortChecks(persona?.checks);
  return {
    name: String(persona?.name || ""),
    username: String(persona?.username || ""),
    status: String(persona?.status || "skipped"),
    failing: only(checks, "fail"),
    skipped: only(checks, "skipped"),
    passing: only(checks, "pass"),
    total: checks.length,
    screenshots: screenshotsOf(persona),
    refusals: persona?.refusals_observed || [],
    writes: persona?.writes_performed || [],
    notes: persona?.notes || [],
    truncated: persona?.truncated || null,
    calls: persona?.calls || null,
    accessLine: accessLine(persona?.access),
  };
}

/**
 * The report, for a screen.
 *
 * `body` is the 200 from `GET /sandboxes/{id}/qa`: `{run, gate, label,
 * does_not_prove, runs}`. The kinds, in the order a reader should check them:
 *
 *  - **none**: nothing has been run for this sandbox. Not a pass and not a fail.
 *  - **running**: in progress.
 *  - **error**: the run did not complete. The platform's, not the app's; it is
 *    neither a finding nor a pass.
 *  - **failed**: a finding about the app.
 *  - **passed**: every persona passed *for the build and manifest being asked
 *    for now*. A `passed` run bound to an older manifest hash is `earlier`: it
 *    describes a ceiling nobody is asking for, and is not shown as a pass.
 *
 * `bound` is false when the run was made for an older manifest than the request
 * has now, and is said on the figure whatever the status.
 */
export function qaView(body) {
  const run = body?.run || null;
  const gate = body?.gate || {};
  if (!run) {
    return { kind: "none", reason: String(gate.reason || ""), runs: body?.runs || [] };
  }
  const bound = run.bound_to_current !== false;
  let kind = String(run.status || "error");
  if (!["running", "error", "failed", "passed"].includes(kind)) kind = "error";
  if (kind === "passed" && (!bound || gate.passed_for_current === false)) kind = "earlier";

  const runWide = sortChecks(run.checks);
  const personas = (run.personas || [])
    .map((p, index) => ({ view: personaView(p), index }))
    .sort((a, b) =>
      (PERSONA_ORDER[a.view.status] ?? 2) - (PERSONA_ORDER[b.view.status] ?? 2)
      || a.index - b.index)
    .map(({ view }) => view);

  // Every failing check in one list, run-wide first, then by persona, so the
  // top of the report is the findings and the rest is context.
  const findings = [
    ...only(runWide, "fail").map((check) => ({ ...check, persona: check.persona ?? null })),
    ...personas.flatMap((p) => p.failing.map((check) => ({ ...check, persona: check.persona ?? p.name }))),
  ];

  const s = run.summary || {};
  // A failure the kernel marked `inconclusive` (LO-193) could not be judged and is
  // not something the app did: the egress gateway was not confirmed working, so the
  // log could not say. It is still a failure and still blocks a pass.
  const inconclusive = findings.filter((f) => f.inconclusive === true).length;
  const totals = {
    personas: Number(s.personas ?? personas.length),
    personasPassed: Number(s.personas_passed ?? personas.filter((p) => p.status === "passed").length),
    checks: Number(s.checks ?? 0),
    passed: Number(s.passed ?? 0),
    failed: Number(s.failed ?? findings.length),
    skipped: Number(s.skipped ?? 0),
    inconclusive,
  };

  return {
    kind,
    bound,
    run,
    gate,
    label: String(run.label || body?.label || PASS_LABEL),
    totals,
    inconclusiveOnly: kind === "failed" && findings.length > 0 && inconclusive === findings.length,
    findings,
    runWide: {
      failing: only(runWide, "fail"),
      skipped: only(runWide, "skipped"),
      passing: only(runWide, "pass"),
    },
    personas,
    liveRan: run.live_run?.ran !== false,
    liveSkipped: run.live_run?.ran === false
      ? { reason: String(run.live_run.skipped_reason || ""), detail: String(run.live_run.detail || "") }
      : null,
    error: run.error ? { reason: String(run.error.reason || ""), detail: String(run.error.detail || "") } : null,
    doesNotProve: [...new Set([...(run.does_not_prove || []), ...(body?.does_not_prove || [])])],
    truncated: Boolean(run.truncated),
  };
}

// -- screenshots ----------------------------------------------------------------------

/**
 * The route that answers with a short-lived address for one screenshot.
 *
 * A report names a screenshot by a path inside its run, `screenshots/<persona>/
 * <file>.png`, never an address, and that path is the tail of the route:
 * `/sandboxes/{id}/qa/` + the path, with the report's `run_id` as `?run_id=`
 * (the caller adds it as a parameter). Each segment is encoded and the slashes
 * kept, so the path stays one route the kernel can split again.
 */
export function screenshotRoute(requestId, name) {
  const raw = String(name || "").split("/").filter(Boolean);
  if (!raw.length) throw new Error("the report names no path for it");
  // A browser resolves `..` in a URL before it sends anything, so a path that
  // climbs would ask for a different route and not for a file under this one.
  // The report's own schema forbids it; this refuses it where it would matter.
  if (raw.some((part) => part === "." || part === "..")) {
    throw new Error("the report's path for it tries to climb out of its folder");
  }
  const segments = raw.map(encodeURIComponent);
  return `/sandboxes/${encodeURIComponent(requestId)}/qa/${segments.join("/")}`;
}

/**
 * Why a screenshot could not be had, in what the kernel's code means.
 *
 * Three of the codes are facts about the screenshot and each says something
 * different: it is not listed by any report (so the kernel will not serve it),
 * it was listed and the file is gone, or the name is not one it accepts. Never
 * a blank, and never only a status.
 */
export function screenshotFailure(error) {
  const code = String(error?.error || error?.body?.error || "");
  switch (code) {
    case "screenshot_missing":
      return "This screenshot is no longer stored.";
    case "unknown_screenshot":
      return "No QA report for this sandbox lists this screenshot, so the kernel will not serve it.";
    case "unknown_run":
      return "The run that took this screenshot is no longer there.";
    case "bad_screenshot_name":
      return "The report's name for this screenshot is not one the kernel accepts.";
    case "no_qa_runner":
      return "This kernel has no QA runner configured, so it cannot serve screenshots.";
    default:
      break;
  }
  if (error?.status) {
    return `The kernel answered ${error.status}${code ? ` ${code}` : ""}: ${error.message}`;
  }
  return error?.message || "No reason was given.";
}

/**
 * What the screenshot route answered, as something an `<img>` may be pointed at.
 *
 * `{url, expires_in, content_type}`. Refused rather than guessed at: an address
 * that is not https, or a content type that is not an image, is not put in an
 * `<img>`, and the reader is told the screenshot could not be loaded instead of
 * being shown a broken one.
 */
export function screenshotAnswer(body) {
  const url = String(body?.url || "");
  let parsed = null;
  try {
    parsed = new URL(url);
  } catch {
    parsed = null;
  }
  if (!parsed || parsed.protocol !== "https:") {
    throw new Error("the kernel did not return a web address for it");
  }
  const type = String(body?.content_type || "image/png");
  if (!/^image\//i.test(type)) {
    throw new Error(`the kernel returned ${type}, which is not an image`);
  }
  return { url, type, expiresIn: Number(body?.expires_in) || 0 };
}

// -- the call log ---------------------------------------------------------------------

/** `allowed`, `empty`, or `refused`: three sentences a status code flattens. */
export function outcomeKind(outcome) {
  if (outcome === "allowed") return "allowed";
  if (outcome === "empty") return "empty";
  return "refused";
}

/** What a call was, in a few words, from what the kernel recorded. */
export function callWhat(call) {
  if (call?.verb) return `${call.method || "POST"} ${call.verb}`;
  if (call?.aggregate) return `${call.method || "POST"} aggregate ${call.aggregate}`;
  return String(call?.route || "");
}

/** What a call came back with: the word first, then what backs it. */
export function callResult(call) {
  const kind = outcomeKind(call?.outcome);
  if (kind === "refused") return { kind, word: "refused", code: String(call?.outcome || ""), status: call?.status };
  if (kind === "empty") return { kind, word: "empty", code: "", status: call?.status };
  return { kind, word: "allowed", code: "", status: call?.status };
}

/**
 * The call log's headline: the figures over **every recorded call**, which the
 * kernel does not move with a filter, and the personas to filter by, read from
 * its own summary rather than listed here.
 */
export function callsView(body) {
  const summary = body?.summary || {};
  const byPersona = summary.by_persona || {};
  return {
    total: Number(summary.total ?? body?.recorded ?? 0),
    allowed: Number(summary.allowed ?? 0),
    empty: Number(summary.empty ?? 0),
    refused: Number(summary.refused ?? 0),
    recorded: Number(body?.recorded ?? 0),
    none: Number(body?.recorded ?? 0) === 0,
    personas: Object.entries(byPersona).map(([name, v]) => ({ name, total: Number(v?.total ?? 0) })),
    truncated: Boolean(body?.truncated),
    doesNotProve: body?.does_not_prove || [],
  };
}

// -- narrowing candidates -------------------------------------------------------------

/** What the approve dialog reads when there is nothing to flag. */
export const NO_NARROWING = Object.freeze({
  reads: new Set(), service_reads: new Set(), actions: new Set(), service_actions: new Set(),
  aggregates: new Set(), egress: new Set(),
  other: [], count: 0, strikable: 0, basis: "none", calls: "unread", egress_log: "unread",
});

/**
 * The grants and hosts the sandbox did not use, as the keys the approve dialog
 * strikes by, so a flag and its checkbox can never be about different things.
 *
 * `calls` and `egress` are the two 200 bodies. Three rules:
 *
 *  - **An empty log flags nothing.** With no recorded call the kernel lists
 *    every grant as unused, and says that is a run that has not happened and
 *    not a finding. Flagging all of them would invite striking everything on
 *    the strength of no evidence, so the flags wait for a log with something in
 *    it. The same holds for hosts.
 *  - **Unused is by type or verb, not by tag**, as the kernel judged it.
 *  - **Only what the dialog can strike is strikable.** The approve route takes a
 *    replacement for each of reads, service reads, user writes, service writes,
 *    aggregates and egress hosts (`_approve` in the kernel's api.py), so each of
 *    those has a box and a key here, and the keys are the verb or the aggregate's
 *    name. Anything else the kernel might flag is listed in `other` and is not
 *    offered a checkbox that is not there.
 *
 * It is a reason to ask and never a finding, which is what `basis` carries to
 * the screen: `calls` and `egress` say which logs the flags rest on.
 */
export function narrowing(calls, egress) {
  const callsRecorded = Number(calls?.recorded ?? 0) > 0;
  // A host the sandbox "never tried" is only that if the gateway that would have
  // recorded it was working (LO-193). A gateway the kernel says is not working
  // makes the list a record of what happened to be written, so it flags nothing:
  // an answer with no gateway block is judged as it always was.
  const gateway = gatewayOf(egress);
  const gatewayDown = Boolean(gateway && gateway.known && !gateway.healthy);
  const egressRecorded = Number(egress?.recorded ?? 0) > 0 && !gatewayDown;
  const out = {
    reads: new Set(), service_reads: new Set(), actions: new Set(), service_actions: new Set(),
    aggregates: new Set(), egress: new Set(),
    other: [], count: 0, strikable: 0, basis: "none",
    // What each log said, so a screen can tell "nothing was flagged because
    // nothing was recorded" from "nothing was flagged because it has not been read",
    // and from "because the gateway that records it was not working".
    calls: !calls ? "unread" : callsRecorded ? "recorded" : "empty",
    egress_log: !egress ? "unread" : gatewayDown ? "not_recorded" : egressRecorded ? "recorded" : "empty",
  };
  if (callsRecorded) {
    for (const g of calls.unused_grants || []) {
      if (g.kind === "read" && g.type) out.reads.add(g.type);
      else if (g.kind === "service_read" && g.type) out.service_reads.add(g.type);
      else if (g.kind === "write" && g.verb) out.actions.add(g.verb);
      else if (g.kind === "service_write" && g.verb) out.service_actions.add(g.verb);
      else if (g.kind === "aggregate" && g.name) out.aggregates.add(g.name);
      else out.other.push(g);
    }
  }
  if (egressRecorded) {
    for (const host of egress?.summary?.declared_not_contacted || []) out.egress.add(host);
  }
  out.strikable = out.reads.size + out.service_reads.size + out.actions.size
    + out.service_actions.size + out.aggregates.size + out.egress.size;
  out.count = out.strikable + out.other.length;
  out.basis = callsRecorded && egressRecorded ? "calls and egress"
    : callsRecorded ? "calls" : egressRecorded ? "egress" : "none";
  return out;
}

/** One unused grant, as a short line for a list. */
export function unusedLine(grant) {
  if (grant.kind === "read" && grant.type) return `read ${grant.type}`;
  if (grant.kind === "service_read" && grant.type) return `read ${grant.type}, with no user behind it`;
  if (grant.kind === "write" && grant.verb) return `write ${grant.verb}`;
  if (grant.kind === "service_write" && grant.verb) return `write ${grant.verb}, with no user behind it`;
  if (grant.kind === "aggregate" && grant.name) return `aggregate ${grant.name}`;
  return String(grant.detail || grant.kind || "");
}

// -- egress: declared against observed ------------------------------------------------

/** The gateway's own words for its state, as a chip says them. Never colour alone. */
export const GATEWAY_WORD = {
  healthy: "working",
  silent: "silent",
  recording_failed: "cannot record",
  unconfirmed: "not confirmed",
  never_reported: "never reported",
  unreadable: "state not readable",
};

const READINGS = ["hosts_recorded", "nothing_contacted", "not_recorded"];

/**
 * Whether the gateway that fills the egress log was working (LO-193), as a word, a
 * tone and the kernel's own sentence, or `null` for no answer at all.
 *
 * `known` is false for an answer that has no `gateway` block (a kernel from before
 * it): that is its own state and is never read as a working gateway.
 */
export function gatewayOf(body) {
  if (!body) return null;
  const g = body.gateway;
  if (!g || typeof g !== "object") {
    return {
      known: false, state: "unknown", word: "not reported", tone: "attention", healthy: false,
      detail: "The kernel did not say whether the gateway is working.", lastError: "", lastErrorAt: "",
    };
  }
  const state = String(g.state || "unknown");
  const healthy = g.healthy === true;
  return {
    known: true,
    state,
    word: GATEWAY_WORD[state] || state.replace(/_/g, " "),
    tone: healthy ? "permitted" : "attention",
    healthy,
    detail: String(g.detail || ""),
    lastError: String(g.last_error || ""),
    lastErrorAt: String(g.last_error_at || ""),
  };
}

/**
 * Declared hosts beside observed ones, one row per host, worst first.
 *
 * `body` is the 200 from `GET /sandboxes/{id}/egress`; `declared` is what the
 * request declares now (the kernel's `declared` wins when it is there). Verdicts:
 *
 *  - **undeclared**: contacted and never declared. A failure whatever the
 *    gateway did with it; `letThrough` says whether it did stop it.
 *  - **declared**: declared and contacted. `blocked` carries attempts the
 *    gateway stopped, which a declared host can have if the list changed.
 *  - **never**: declared, a log with something in it, and no attempt. A
 *    narrowing candidate and not a finding.
 *  - **unobserved**: declared, and the log is empty. Nothing was seen, so
 *    nothing is said about the host.
 *  - **unrecorded**: declared, and the gateway that fills the log was not working
 *    (LO-193): the list is empty because nothing was kept, or may be missing what
 *    it did not keep. Not the same as unobserved, and never a candidate.
 *
 * `gateway` is the kernel's block, as a word and a tone (`gatewayOf`), `reading` is
 * the kernel's own account of how to read the list (`hosts_recorded`,
 * `nothing_contacted`, `not_recorded`, or `unknown` for an answer that does not say)
 * and `readingDetail` its sentence. `notRecorded` is an empty list that is not
 * recorded, and `incomplete` is a list with hosts in it from a gateway that was not
 * working. None of it is worked out here.
 */
export function egressView(body, declared = []) {
  const wanted = (Array.isArray(body?.declared) ? body.declared : declared || [])
    .map((h) => String(h).toLowerCase());
  const empty = Number(body?.recorded ?? 0) === 0;
  const gateway = gatewayOf(body);
  const reading = READINGS.includes(body?.reading?.state) ? body.reading.state : "unknown";
  // The gateway that fills this log was not working (LO-193). An empty list is then
  // not recorded and says nothing about the function, and a list with hosts in it
  // may be incomplete: either way a declared host nobody was seen to try is
  // unrecorded, which is not the same as never contacted.
  const gatewayDown = Boolean(gateway && gateway.known && !gateway.healthy);
  const notRecorded = empty && reading === "not_recorded";
  const unseen = empty ? (notRecorded ? "unrecorded" : "unobserved") : (gatewayDown ? "unrecorded" : "never");

  const seen = new Map();
  for (const h of body?.hosts || []) {
    const host = String(h.host || "").toLowerCase();
    if (!host) continue;
    const row = seen.get(host) || { host, attempts: 0, letThrough: 0, blocked: 0, first: "", last: "" };
    const n = Number(h.count || 0);
    row.attempts += n;
    if (h.allowed) row.letThrough += n;
    else row.blocked += n;
    if (!row.first || (h.first_seen && h.first_seen < row.first)) row.first = h.first_seen || row.first;
    if (!row.last || (h.last_seen && h.last_seen > row.last)) row.last = h.last_seen || row.last;
    seen.set(host, row);
  }

  const rows = [];
  for (const row of seen.values()) {
    const declaredHost = wanted.includes(row.host);
    rows.push({ ...row, declared: declaredHost, verdict: declaredHost ? "declared" : "undeclared" });
  }
  for (const host of wanted) {
    if (!seen.has(host)) {
      rows.push({
        host, declared: true, attempts: 0, letThrough: 0, blocked: 0, first: "", last: "",
        verdict: unseen,
      });
    }
  }
  const rank = { undeclared: 0, declared: 1, never: 2, unobserved: 3, unrecorded: 3 };
  rows.sort((a, b) => rank[a.verdict] - rank[b.verdict] || a.host.localeCompare(b.host));

  return {
    rows,
    empty,
    gateway,
    reading,
    readingDetail: String(body?.reading?.detail || ""),
    notRecorded,
    incomplete: !empty && gatewayDown,
    recorded: Number(body?.recorded ?? 0),
    failures: rows.filter((r) => r.verdict === "undeclared"),
    never: rows.filter((r) => r.verdict === "never"),
    kernelHosts: (body?.kernel_hosts || []).map((h) => String(h.host || "")).filter(Boolean),
    truncated: Boolean(body?.truncated),
    doesNotProve: body?.does_not_prove || [],
  };
}

// -- the live link --------------------------------------------------------------------

/**
 * The sandbox's address with the approver's own access token in its fragment.
 *
 * The kit running in the sandbox reads `#viewer_token=<token>`, moves it to the
 * tab's session storage and takes it out of the address bar (LO-188). It is the
 * *viewer's* token, which is what lets the kit sign a persona in on the viewer's
 * behalf; a persona's credentials are never in a link. In a fragment, so the
 * browser sends it to nobody. Empty when there is nothing to open or nobody to
 * sign in as, so a link is never offered that would land on a sandbox that
 * cannot sign anyone in.
 */
export function liveLink(appUrl, token) {
  const base = String(appUrl || "");
  const viewer = String(token || "");
  if (!base || !viewer) return "";
  return `${base}${base.includes("#") ? "&" : "#"}viewer_token=${encodeURIComponent(viewer)}`;
}
